/**
 * Builds every service of the GUI.
 *
 * The services are subsystems (`subsystems.ts`): each declares what it is built from, and
 * `shared/subsystems.ts` starts them in an order that respects those needs, so nothing here
 * depends on writing them in the right order. Then their commands go into the command
 * registry, the config is applied (theme, layout, labels) as it changes, and the plugins'
 * windows and commands follow the plugin list (`pluginWindows.ts`). Call inside a Solid root.
 */
import { tauriApi, tauriFolderPicker, tauriTransport } from "../client/tauri";
import { websocketTransport, type Transport } from "../client/client";
import { accountsApi, isApiError, sessionFor } from "../client/accounts";
import { ownSocketUrl, pageSpace, remoteLink, SpaceRefusal, spaceSocketUrl, type SpaceLink } from "../client/space";
import { wasmTransport } from "../client/wasm";
import type { DirectoryHandle } from "../shared/folders";
import { startSubsystems } from "../shared/subsystems";
import { installConfig } from "./configApply";
import type { App } from "./context";
import { installPluginWindows } from "./pluginWindows";
import { shellMenus } from "./menus";
import { SUBSYSTEMS, type Platform, type Services } from "./subsystems";
import { WINDOWS, builtInLayout } from "./windows";

/** Whether a server served this page and it talks to that server's cores (not the standalone build, not the desktop app). */
export function servedPage(): boolean {
  return import.meta.env.VITE_CORE !== "wasm" && !tauriApi();
}

/** The space this page works in: the remote one it was opened with, or (served at `/s/<space>`) its own. */
function spaceOfPage(remote: SpaceLink | null): SpaceLink | null {
  return remote ?? (servedPage() ? pageSpace(location) : null);
}

/**
 * The address of `space`'s WebSocket with a fresh one-time ticket, bought with this browser's
 * session there before each connection (so the token never sits in an address). Signed out
 * there, or refused: a {@link SpaceRefusal} saying why (the page stops reconnecting).
 */
async function spaceAddress(space: SpaceLink): Promise<string> {
  const token = sessionFor(space.server)?.token;
  if (!token) return spaceSocketUrl(space, undefined);
  try {
    const { ticket, socket } = await accountsApi(space.server, token).spaceTicket(space.space);
    return spaceSocketUrl(space, ticket, socket);
  } catch (error) {
    if (isApiError(error) && error.status === 401) throw new SpaceRefusal(error.message);
    throw error;
  }
}

/**
 * How this page reaches the core: a space (the remote one it was opened with `?remote=<space
 * link>`, from the desktop app or the standalone build, whose own core waits for it to come
 * back; or the page's own `/s/<space>`) over a WebSocket with this browser's session on that
 * server; otherwise started inside the page for the standalone build (`npm run build:wasm`,
 * `VITE_CORE=wasm`), through Tauri inside the desktop app, otherwise over a WebSocket to the
 * page's server's own core.
 */
export function defaultTransport(remote: SpaceLink | null = remoteLink(location.search)): Transport {
  const space = spaceOfPage(remote);
  if (space) return websocketTransport(() => spaceAddress(space));
  if (import.meta.env.VITE_CORE === "wasm") return wasmTransport();
  const tauri = tauriApi();
  if (tauri) return tauriTransport(tauri);
  return websocketTransport(ownSocketUrl(location));
}

/** The browser's folder picker (`showDirectoryPicker`, Chrome and Edge), where there is one. */
function browserFolderPicker(): (() => Promise<DirectoryHandle>) | undefined {
  const picker = (globalThis as { showDirectoryPicker?: (options?: { mode?: string }) => Promise<DirectoryHandle> }).showDirectoryPicker;
  return picker ? () => picker.call(globalThis, { mode: "read" }) : undefined;
}

/** What this page can do, reaching the core through `transport` (a remote space's with `remote`). */
export function currentPlatform(remote: SpaceLink | null = remoteLink(location.search), transport: Transport = defaultTransport(remote)): Platform {
  // The standalone build runs the core in the page: it has no disk, the page reads folders. A
  // remote space's core reads its server's disk, not this computer's: the page reads them too.
  const readsFolders = import.meta.env.VITE_CORE !== "wasm" && !remote;
  return {
    transport,
    remote,
    page: servedPage() ? pageSpace(location) : null,
    served: servedPage(),
    readsFolders,
    pickFolder: readsFolders ? tauriFolderPicker(tauriApi()) : undefined,
    directoryPicker: readsFolders ? undefined : browserFolderPicker(),
  };
}

/** Creates the services, connected to the core through `transport`. */
export function createServices(transport?: Transport): App {
  const remote = remoteLink(location.search);
  const started = startSubsystems<Services>(SUBSYSTEMS, { platform: currentPlatform(remote, transport ?? defaultTransport(remote)), windows: { list: WINDOWS, layout: builtInLayout } });
  const { platform: _platform, windows: _windows, ...app } = started.all;
  for (const set of started.commands) app.commands.add(set);
  app.shell.addMenus([...started.menus, ...shellMenus(app)]);
  app.shell.addToolbar(started.toolbar);
  installConfig(app);
  installPluginWindows(app);
  return app;
}
